import React, { useRef, useEffect, useCallback } from 'react'
import * as d3 from 'd3'
import { BiSemanalStore, Periodo, PeriodoRowModel } from './BiComparativoSemanal'

const MARGIN = { top: 28, right: 80, bottom: 64, left: 56 }
const HEIGHT = 320

const BAR_COLOR_NORMAL = '#4f8ef7'
const BAR_COLOR_HOLIDAY = '#f59e0b'
const BAR_COLOR_ANNOTATION = '#6366f1'
const BAR_COLOR_CURRENT = '#93c5fd'

export function SemanalChart() {
  const { rows, metric, totalAverage, periodo } = BiSemanalStore.useState()
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const rowsRef = useRef(rows)
  const metricRef = useRef(metric)
  const totalAverageRef = useRef(totalAverage)
  const periodoRef = useRef(periodo)

  rowsRef.current = rows
  metricRef.current = metric
  totalAverageRef.current = totalAverage
  periodoRef.current = periodo

  const draw = useCallback(() => {
    const rows = rowsRef.current
    const metric = metricRef.current
    const totalAverage = totalAverageRef.current
    const periodo = periodoRef.current
    if (!svgRef.current || !containerRef.current) return
    if (rows.length === 0) {
      d3.select(svgRef.current).selectAll('*').remove()
      return
    }
    const averageValue = totalAverage ? totalAverage[metric] : null

    const chronological = [...rows].reverse()
    svgRef.current.setAttribute('width', '0')
    const width = containerRef.current.clientWidth
    const innerWidth = width - MARGIN.left - MARGIN.right
    const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    svg.attr('width', width).attr('height', HEIGHT)

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`)

    const xScale = d3
      .scaleBand()
      .domain(chronological.map((w) => w.periodStart))
      .range([0, innerWidth])
      .padding(0.25)

    const maxValue = d3.max(chronological, (w) => w[metric]) ?? 0
    const yScale = d3.scaleLinear().domain([0, maxValue * 1.18]).range([innerHeight, 0])

    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(
        d3
          .axisBottom(xScale)
          .tickFormat((periodStart) => formatShortPeriod(periodStart as string, periodo))
      )
      .selectAll('text')
      .attr('transform', 'rotate(-40)')
      .style('text-anchor', 'end')
      .attr('font-size', 11)

    g.append('g')
      .call(
        d3
          .axisLeft(yScale)
          .ticks(5)
          .tickFormat((v) => formatTickValue(Number(v), metric))
      )
      .attr('font-size', 11)

    g.selectAll('rect')
      .data(chronological)
      .join('rect')
      .attr('x', (w) => xScale(w.periodStart)!)
      .attr('y', (w) => yScale(w[metric]))
      .attr('width', xScale.bandwidth())
      .attr('height', (w) => innerHeight - yScale(w[metric]))
      .attr('fill', (w) => resolveBarColor(w))
      .attr('rx', 3)

    if (averageValue !== null && averageValue > 0) {
      const y = yScale(averageValue)
      g.append('line')
        .attr('x1', 0)
        .attr('x2', innerWidth)
        .attr('y1', y)
        .attr('y2', y)
        .attr('stroke', '#ef4444')
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '5,4')
        .attr('opacity', 0.6)

      g.append('text')
        .attr('x', innerWidth + 4)
        .attr('y', y + 4)
        .attr('font-size', 10)
        .attr('fill', '#ef4444')
        .attr('opacity', 0.6)
        .text(`Ø ${formatLabelValue(averageValue, metric)}`)
    }

    g.selectAll('.bar-label')
      .data(chronological)
      .join('text')
      .attr('class', 'bar-label')
      .attr('x', (w) => xScale(w.periodStart)! + xScale.bandwidth() / 2)
      .attr('y', (w) => yScale(w[metric]) - 5)
      .attr('text-anchor', 'middle')
      .attr('font-size', 10)
      .attr('fill', '#374151')
      .text((w) => formatLabelValue(w[metric], metric))
  }, [])

  useEffect(() => {
    draw()
  }, [rows, metric, totalAverage, periodo, draw])

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>
    function handleResize() {
      clearTimeout(timeout)
      timeout = setTimeout(draw, 150)
    }
    window.addEventListener('resize', handleResize)
    return () => {
      clearTimeout(timeout)
      window.removeEventListener('resize', handleResize)
    }
  }, [draw])

  return (
    <div ref={containerRef} style={{ width: '100%', overflow: 'hidden' }}>
      <svg ref={svgRef} style={{ display: 'block' }} />
    </div>
  )
}

function resolveBarColor(w: PeriodoRowModel) {
  if (w.isCurrent) return BAR_COLOR_CURRENT
  if (w.holidayNames.length > 0) return BAR_COLOR_HOLIDAY
  if (w.annotation !== null) return BAR_COLOR_ANNOTATION
  return BAR_COLOR_NORMAL
}

const MONTH_ABBREVIATIONS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']

function formatShortPeriod(periodStart: string, periodo: Periodo): string {
  const date = new Date(periodStart + 'T00:00:00')
  if (periodo === 'mes') {
    return `${MONTH_ABBREVIATIONS[date.getMonth()]}/${String(date.getFullYear()).slice(2)}`
  }
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
}

function formatTickValue(value: number, metric: string): string {
  if (metric === 'valorNewDriverSum') {
    return value >= 1000 ? `${(value / 1000).toFixed(0)}k` : String(value)
  }
  return String(value)
}

function formatLabelValue(value: number, metric: string): string {
  if (metric === 'valorNewDriverSum') {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value)
  }
  return String(value)
}
